/* eslint-disable camelcase */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';

import { withRouter } from 'react-router-dom';
import { Container as SemanticContainer } from 'semantic-ui-react';
import { Helmet } from 'react-helmet';

import withRootQuery from '~/modules/core/utils/relayHelpers/withRootQuery';
import Loader from '~/modules/coreUI/components/basic/Loader';
import ProductReviews from '~/modules/ecommerceProducts/containers/ProductReviews';

import VendorInfo from '../VendorInfo';

import ProductImageSlider from './areas/ProductImageSlider';
import Options from './areas/Options';
import ShareButtons from '~/modules/ecommerceCoreUI/components/ShareButtons/ShareButtons';
import Title from './areas/Title';
import ActionButtons from './areas/ActionButtons';
import ProductTabs from './areas/ProductTabs';
import OfferDescription from './areas/OfferDescription';
import ProductAccordion from './areas/ProductAccordion';
import SizeChart from './areas/SizeChart';
import ProductPrice from './areas/Price';
import VendorDetails from './areas/VendorDetails';
import ProductSummary from './areas/ProductSummary';
import ProductOffers from './areas/ProductOffers';
import PackageVariants from './areas/PackageVariants';
import OfferedVariants from './areas/OfferedVariants';
import ProductDetailsBreadcrumbs from '~/modules/ecommerceCoreUI/components/breadcrumbs/ProductDetailsBreadcrumbs';
import BuyingOptionsLink from './areas/BuyingOptionsLink';
import SellingOptionLink from './areas/SellingOptionLink';
import UnavailableProductLabel from './areas/UnavailableProductLabel';
import CartActions from './vendor/areas/CartActions';
import Layout from './layouts/Layout';

import ServicePackageProvider from './ServicePackageProvider';
import withMedia from '~/modules/core/utils/mediaHelpers/withMedia';
import ProductDetailsQuery from './vendor/productDetailsQuery/ProductDetailsQuery';
import RelatedProducts from '~/modules/ecommerceCoreUI/components/productDetails/areas/RelatedProducts';

const ElectricLoader = () => <Loader height="750px" />;

class ProductDetailsPage extends React.PureComponent {
  state = {
    shopsLoaded: false,
  }

  render = () => {
    const {
      queryResult,
      match,
      isLoading,
      media,
    } = this.props;
    const fromShops = !!match.params.vendorID;
    const hasSpecs = !!(queryResult?.variant.result.made_in?.name
        || queryResult?.variant.result.brand?.name
        || (queryResult?.variant.result.product.product_properties.length > 0));

    const getVariant = (queryResult_, fromShop_, rootVariants, vendorVariants) => {
      if (queryResult_ && fromShop_) {
        return vendorVariants;
      }
      if (queryResult_ && !fromShop_) {
        return rootVariants;
      }
      if (rootVariants) {
        return rootVariants;
      }
      return null;
    };

    return (
      <Fragment>
        <Helmet>
          {queryResult?.variant.result.product.meta_title && <title>{queryResult?.variant.result.product.meta_title}</title>}
          {queryResult?.variant.result.product.meta_description && <meta name="description" content={queryResult?.variant.result.product.meta_description} />}
          {queryResult?.variant.result.product.meta_keywords && <meta name="keywords" content={queryResult?.variant.result.product.meta_keywords} />}
        </Helmet>
        {queryResult ? (
          <ProductDetailsBreadcrumbs
            shop={queryResult.variant.result.vendor}
            category={queryResult.variant.result.categories[0]}
            variant={queryResult.variant.result}
            fromShops={fromShops}
          />
        ) : null}
        <ServicePackageProvider>
          <SizeChart
            onRef={(ref) => {
              this.sizeChart = ref;
            }}
            sizeChart={queryResult?.variant.result.product.size_chart}
          />
          {queryResult && fromShops && (
            <SemanticContainer>
              <VendorInfo {...queryResult.variant.result.vendor} />
            </SemanticContainer>
          )}
          <Layout
            loader={isLoading && <ElectricLoader />}
            productAccordion={
              queryResult && !queryResult?.variant?.result?.has_offer && (
                <ProductAccordion
                  fromShops={queryResult?.variant?.result?.eligible_stock ? fromShops : true}
                  description={queryResult?.variant.result.description}
                  specifications={queryResult?.variant.result.product.product_properties}
                  madeIn={queryResult?.variant.result.made_in?.name}
                  brand={queryResult?.variant.result.brand?.name}
                  dimensions={queryResult?.variant.result.product.dimensions}
                  weight={queryResult?.variant.result.product.weight}
                  onShopsLoaded={() => { this.setState({ shopsLoaded: true }); }}
                  hasSpecs={hasSpecs}
                />
              )
            }
            productTabs={(queryResult && !queryResult?.variant?.result?.has_offer) ? (
              <ProductTabs
                description={queryResult?.variant.result.description}
                specifications={queryResult?.variant.result.product.product_properties}
                madeIn={queryResult?.variant.result.made_in?.name}
                brand={queryResult?.variant.result.brand?.name}
                dimensions={queryResult?.variant.result.product.dimensions}
                weight={queryResult?.variant.result.product.weight}
                fromShops={queryResult?.variant?.result?.eligible_stock ? fromShops : true}
                onShopsLoaded={() => { this.setState({ shopsLoaded: true }); }}
                hasSpecs={hasSpecs}
              />
            ) : (
              null
            )}
            OfferDescription={
              queryResult?.variant?.result?.has_offer ? (
                <OfferDescription description={queryResult?.variant.result.description} />
              ) : (
                null
              )
            }
            productImageSlider={queryResult ? (
              <ProductImageSlider
                images={queryResult?.variant.result.images_attachments.map(x => [x.url_large, x.url_original, x.alt])}
              />
            ) : (
              null
            )}
            productTitle={queryResult ? (
              <Title
                title={queryResult?.variant.result.name}
                ratingCount={queryResult?.variant.result.rating_count}
                averageRating={queryResult?.variant.result.avg_rating}
              />
            ) : (
              null
            )}
            offeredVariants={queryResult?.variant?.result?.has_offer && queryResult?.variant?.result?.offered_variants.length > 0 ? (
              <OfferedVariants offeredVariants={queryResult?.variant?.result?.offered_variants} />
            ) : (
              null
            )}
            productPrice={queryResult && queryResult?.variant?.result?.eligible_stock ? (
              <ProductPrice
                price={queryResult?.variant.result.price}
                oldPrice={queryResult?.variant.result.original_price}
                taxStatus={queryResult?.variant.result.product.tax_status}
                showStartFromLabel={!fromShops || !queryResult?.variant?.result?.has_offer}
              />
            ) : (
              !queryResult?.variant?.result?.eligible_stock && <UnavailableProductLabel />
            )}
            vendorDetails={
              queryResult && fromShops && <VendorDetails variant={queryResult.variant.result} isEligibleStock={queryResult?.variant?.result?.eligible_stock} />
            }
            shareButtons={<ShareButtons vertical label={i18next.t('ecommerceCoreUI:Product.share')} />}
            options={
              queryResult
              && queryResult.variant.result.product.root_variants.length > 0
              && queryResult?.variant?.result?.eligible_stock ? (
                <Options
                  variants={getVariant(
                    queryResult,
                    fromShops,
                    queryResult.variant.result.product.root_variants,
                    queryResult.variant.result.vendor_available_variants,
                  )}
                  optionTypes={queryResult?.variant.result.product.option_types}
                  initVariant={queryResult?.variant.result}
                  sizeChartToggle={() => this.sizeChart.show()}
                  hasSizeChart={queryResult?.variant.result.product.size_chart.length > 0}
                />
                ) : (
                  !queryResult?.variant?.result?.eligible_stock && (
                    <UnavailableProductLabel isRow={false} size={['xxl', 'xxl', 'header', 'header']} />
                  ))}
            actionButtons={queryResult && <ActionButtons itemInfo={queryResult.variant.result} />}
            mobileShareButtons={<ShareButtons label={i18next.t('ecommerceCoreUI:Product.share')} />}
            cartActions={queryResult && fromShops && queryResult?.variant?.result?.eligible_stock && (
              <CartActions
                variantId={queryResult.variant.result.id}
                variant={queryResult.variant.result}
                vertical={fromShops && media.minDesktop}
              />
            )}
            productOffers={
              queryResult?.variant?.result?.available_in_offers.length > 0 ? (
                <ProductOffers offers={queryResult.variant.result.available_in_offers} />
              ) : (
                null
              )
            }
            productSummary={queryResult?.variant?.result?.summary ? <ProductSummary summary={queryResult.variant.result.summary} /> : undefined}
            packageVariants={(queryResult && queryResult?.variant?.result?.eligible_stock && (queryResult.variant.result.product_type === 'package_service')) ? (
              <PackageVariants
                variantServices={queryResult.variant.result.variant_services}
                requiredServicesAmount={queryResult.variant.result.required_services_amount}
              />
            ) : (
              null
            )}
            productReviews={(
              <ProductReviews
                currentUserReview={queryResult?.variant.result.current_user_review}
                ratingCount={queryResult?.variant.result.rating_count}
                averageRating={queryResult?.variant.result.avg_rating}
                variant_id={queryResult?.variant.result.ref_id}
                shopsLoaded={this.state.shopsLoaded || fromShops}
                isLoaded={!!queryResult}
              />
            )}
            BuyingOptionsLink={
               (!queryResult?.variant?.result?.eligible_stock
               && queryResult?.variant?.result?.product_vendor_variants.length > 0) ? (
                 <BuyingOptionsLink vendor={queryResult.variant.result.product_vendor_variants} />
                 ) : (
                   null
                 )}
            relatedProducts={(
              <RelatedProducts
                queryResult={queryResult?.variant.result.product}
              />
            )}
            SellingOptionLink={
              (queryResult?.variant?.result?.eligible_stock
               && !fromShops) ? (
                 <SellingOptionLink />
                ) : (
                  null
                )
            }
          />
        </ServicePackageProvider>
      </Fragment>
    );
  };
}

ProductDetailsPage.defaultProps = {
  queryResult: null,
};

ProductDetailsPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      vendorID: PropTypes.string,
      vendorName: PropTypes.string,
    }),
  }).isRequired,
  queryResult: PropTypes.shape({}),
};

export default withMedia(withRouter(
  withRootQuery(
    ProductDetailsPage,
    ProductDetailsQuery,
    ProductDetailsPage,
    props => ({
      input: {
        root_variant_id: parseInt(props.match.params.slug.split('-')[0], 10),
        vendor_id: parseInt(props.match.params.vendorID, 10),
        mark_viewed: true,
      },
    }),
    { force: true },
  ),
));
